import { useEffect, useMemo, useState } from "react";

const TOTAL_PAGE_PER_FETCH = 5;

const useInfinityPagination = ({
  fetchFunction,
  resultKey = "results",
  errCallback,
  filter,
  page,
  rowsPerPage,
  setPagination,
  setKeywordsUsedForSearch = () => {},
  setRelatedKeywords = () => {},
}) => {
  const [data, setData] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentMaxPage, setCurrentMaxPage] = useState(0);

  useEffect(() => {
    setPagination({
      page: 1,
      rowsPerPage,
    });
    setIsFetching(true);
    setData({});
    setCurrentMaxPage(0);
    setHasNextPage(true);
  }, [rowsPerPage, JSON.stringify(filter)]);

  const { pageToFetch, numberOfPageToFetch } = useMemo(() => {
    const _page = page - 1;
    let pageToFetch = _page;
    for (let i = _page; i < _page + TOTAL_PAGE_PER_FETCH + 1; i++) {
      pageToFetch = i;
      if (!data[i]) {
        break;
      }
    }

    const numberOfPageToFetch = _page + TOTAL_PAGE_PER_FETCH - pageToFetch;
    return { pageToFetch, numberOfPageToFetch };
  }, [page, rowsPerPage, data]);

  useEffect(() => {
    if (!!numberOfPageToFetch && (hasNextPage || pageToFetch < currentMaxPage))
      fetchFunction({
        ...filter,
        page: pageToFetch,
        rowsPerPage,
        numPages: numberOfPageToFetch,
      })
        .then((res) => {
          setData((prevData) => {
            const newData = { ...prevData };
            let i = 0;
            for (; i < numberOfPageToFetch; i++) {
              const pageData = (res?.[resultKey] || []).slice(
                i * rowsPerPage,
                (i + 1) * rowsPerPage,
              );
              if (pageData.length > 0) {
                newData[pageToFetch + i] = pageData;
              } else {
                break;
              }
            }

            setCurrentMaxPage((prev) => Math.max(prev, pageToFetch + i));

            return newData;
          });
          setHasNextPage(res?.has_next_page);
          if (res?.related_keywords?.length > 0) {
            setRelatedKeywords(res.related_keywords);
          }
          if (res?.keywords_used_for_search?.length > 0) {
            setKeywordsUsedForSearch(res.keywords_used_for_search);
          }
          setIsFetching(false);
        })
        .catch((e) => {
          setIsFetching(false);
          errCallback?.(e);
        });
  }, [JSON.stringify(filter), pageToFetch, numberOfPageToFetch, hasNextPage]);

  return {
    data,
    isLoading: page === 1 && isFetching,
    hasNextPage,
    currentMaxPage,
  };
};

export default useInfinityPagination;
